import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { IoArrowForward, IoCheckmarkCircleOutline, IoChevronBack, IoChevronForward } from "react-icons/io5";

// Asset imports
import muttonImg from "../assets/banner3.jpg";
import avocadoImg from "../assets/banner6.jpg";
import mangoImg from "../assets/mangoes.jpeg";
import chilliImg from "../assets/chilli.jpeg";
import bananaImg from "../assets/bananas.jpeg";
import pineappleImg from "../assets/pineapples.jpeg";
import cassavaImg from "../assets/cassava.jpeg";

const PRODUCT_DATA = [
  {
    id: "mutton",
    title: "Premium Mutton",
    description: "Export-grade cuts, processed with strict adherence to international hygiene and halal standards.",
    image: muttonImg,
    accent: "#102030",
    badge: "Hygienic Process",
  },
  {
    id: "avocados",
    title: "Fresh Avocados",
    description: "Sustainably farmed Tanzanian Hass avocados, hand-picked and graded for global markets.",
    image: avocadoImg,
    accent: "#1f3c5a",
    badge: "Export Quality",
  }
];

const FRUIT_SLIDER_DATA = [
  { id: "mangoes", title: "Tropical Mangoes", description: "Fiberless, sun-ripened Tanzanian mangoes, sourced from sustainable orchards for peak freshness.", image: mangoImg },
  { id: "chilli", title: "Fresh Chillies", description: "Vibrant and pungent Tanzanian chillies, sorted and packed under strict cold-chain management.", image: chilliImg },
  { id: "bananas", title: "Premium Bananas", description: "Nutrient-rich varieties grown through ethical trade practices, delivered via efficient logistics.", image: bananaImg },
  { id: "pineapples", title: "Sweet Pineapples", description: "High-brix Tanzanian pineapples, known for exceptional flavor and sustainable farming roots.", image: pineappleImg },
  { id: "cassava", title: "Fresh Cassava", description: "Premium-grade Tanzanian cassava, harvested at peak maturity and processed to ensure high starch content and a clean, fiber-free texture.", image: cassavaImg },
];

const OPERATIONS_STEPS = [
  "Ethical Sourcing",
  "Hygienic Handling",
  "Custom Packaging",
  "Global Logistics",
];

export default function ServicesBanner1() {
  const { navTo } = useContext(AppContext);
  const [isReady, setIsReady] = useState(false);
  const [activeFruit, setActiveFruit] = useState(0);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const handleOpenService = (subItem) => {
    navTo({ item: "services", subItem });
  };

  const nextFruit = () => setActiveFruit((prev) => (prev === FRUIT_SLIDER_DATA.length - 1 ? 0 : prev + 1));
  const prevFruit = () => setActiveFruit((prev) => (prev === 0 ? FRUIT_SLIDER_DATA.length - 1 : prev - 1));

  if (!isReady) {
    return <div className="d-flex justify-content-center py-5">{getInlineLoader()}</div>;
  }

  return (
    <section className="" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)", marginTop: "var(--topBarHeight)" }}>
      <div className="container py-5">
        
        {/* Header Section */}
        <div className="text-center mb-5 pb-3">
          <h2 className="display-5 font-bold mb-3" style={{ color: "#1d3f19", letterSpacing: "-0.02em" }}>
            Our Services
          </h2>
          <p className="text-muted font-semi-bold mx-auto fs-5" style={{ maxWidth: "600px", lineHeight: "1.6" }}>
            Connecting Tanzanian excellence to the world through a transparent and high-standard supply chain.
          </p>
        </div>

        {/* 1. Core Product Grid (Mutton & Avocados) */}
        <div className="row g-4 justify-content-center mb-5">
          {PRODUCT_DATA.map((product, index) => (
            <div key={product.id} className="col-md-6">
              <div 
                className="group border-0 rounded-4 overflow-hidden shadow-sm bg-white h-100 transition-all"
                style={{ 
                    cursor: "pointer", 
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    border: "1px solid #e2e8f0" 
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => handleOpenService(product.id)}
              >
                <div className="position-relative overflow-hidden" style={{ height: "300px" }}>
                  <img 
                    src={product.image} 
                    alt={product.title} 
                    className="w-100 h-100 object-fit-cover transition-all"
                    style={{ filter: "brightness(0.9)" }}
                  />
                  <div className="position-absolute top-0 start-0 m-3">
                    <span className="badge rounded-pill px-3 py-2" style={{ background: "rgba(255,255,255,0.9)", color: "#1e293b", backdropFilter: "blur(4px)" }}>
                      {product.badge}
                    </span>
                  </div>
                </div>

                <div className="p-4 p-lg-5">
                  <h3 className="h3 fw-bold mb-3" style={{ color: "#1e293b" }}>{product.title}</h3>
                  <p className="text-muted mb-4 fs-6">{product.description}</p>
                  
                  <div className="d-flex align-items-center gap-2 fw-bold" style={{ color: product.accent }}>
                    <span>Explore Specifications</span>
                    <IoArrowForward className="mt-1 transition-all" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 2. Expanded Fruit Showcase (New Slider Section) */}
        <div className="bg-white rounded-4 border-0 shadow-sm overflow-hidden mb-5">
          <div className="row g-0 align-items-center">
            <div className="col-lg-6">
              <div className="position-relative" style={{ height: "400px" }}>
                <img 
                  src={FRUIT_SLIDER_DATA[activeFruit].image} 
                  alt={FRUIT_SLIDER_DATA[activeFruit].title}
                  className="w-100 h-100 object-fit-cover transition-all"
                  style={{ transition: "0.5s ease" }}
                />
              </div>
            </div>
            <div className="col-lg-6 p-4 p-lg-5">
              <div className="mb-4">
                <small className="text-uppercase fw-bold text-success mb-2 d-block" style={{ letterSpacing: "0.1em" }}>Premium Produce</small>
                <h3 className="h2 fw-bold mb-3" style={{ color: "#1e293b" }}>{FRUIT_SLIDER_DATA[activeFruit].title}</h3>
                <p className="text-muted fs-5 mb-4">{FRUIT_SLIDER_DATA[activeFruit].description}</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <button onClick={prevFruit} className="btn btn-outline-dark rounded-circle p-2 d-flex align-items-center justify-content-center">
                  <IoChevronBack size={20} />
                </button>
                <span className="fw-bold text-muted">{activeFruit + 1} / {FRUIT_SLIDER_DATA.length}</span>
                <button onClick={nextFruit} className="btn btn-outline-dark rounded-circle p-2 d-flex align-items-center justify-content-center">
                  <IoChevronForward size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Operations Section */}
        <div className="mt-5 p-4 p-lg-5 rounded-4 border-0 shadow-sm" style={{ background: "#1e293b" }}>
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <h4 className="text-secondary font-bold h2 mb-3">Our Operations</h4>
              <p className="text-light opacity-75 mb-0">
                A structured supply chain ensuring farm-to-table integrity.
              </p>
            </div>
            <div className="col-lg-7">
              <div className="row g-3">
                {OPERATIONS_STEPS.map((step, i) => (
                  <div key={i} className="col-sm-6">
                    <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: "rgba(255,255,255,0.05)" }}>
                      <IoCheckmarkCircleOutline className="text-info" size={24} />
                      <span className="text-white fw-medium">{step}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}